import { FC, useEffect, useState } from 'react';
import { Dialog } from 'radix-ui';
import { twMerge } from 'tailwind-merge';
import { Button, Typography } from '@dreamteam-monorepo/ui';

/**
 * Design export for the podium illustration. The panel is dropped entirely when
 * the file is not there, so the modal still reads correctly without it.
 */
export const TEAM_UPDATING_IMAGE_URL = '/team-updating.png';

export interface TeamUpdatingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onViewLeagues: () => void;
  onViewMatchCentre: () => void;
  /** Override the illustration; pass `null` to render the modal without one. */
  imageUrl?: string | null;
}

/**
 * "Your team is updating" - the between-gameweeks notice.
 *
 * Shown while the finished gameweek is being settled: the caller decides that
 * from the current gameweek's deadline (see `useTeamUpdatingWindow`) and the
 * `enable_team_transition_popup` app setting. There is no close button, and it
 * is deliberately non-dismissible - Escape and clicking the overlay are both
 * blocked - so the team cannot be edited mid-settlement. The only ways out are
 * the two CTAs (which navigate away) or an admin turning the app setting off
 * once the transition is done.
 */
export const TeamUpdatingModal: FC<TeamUpdatingModalProps> = ({
  open,
  onOpenChange,
  onViewLeagues,
  onViewMatchCentre,
  imageUrl = TEAM_UPDATING_IMAGE_URL,
}) => {
  const [hasImageFailed, setHasImageFailed] = useState(false);

  useEffect(() => {
    setHasImageFailed(false);
  }, [imageUrl]);

  const description =
    'The previous Gameweek has finished and we are currently updating your team. This should only take a few minutes.';
  const showImage = !!imageUrl && !hasImageFailed;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[130] bg-[--overlay-primary]" />
        <Dialog.Content
          onEscapeKeyDown={event => event.preventDefault()}
          onPointerDownOutside={event => event.preventDefault()}
          onInteractOutside={event => event.preventDefault()}
          className={twMerge(
            'fixed left-1/2 top-1/2 z-[140] w-[440px] max-w-[92vw] max-h-[calc(100vh-24px)]',
            'overflow-y-auto -translate-x-1/2 -translate-y-1/2 rounded-[--corner-md] bg-white',
            'shadow-[var(--shadow-6)] focus:outline-none'
          )}
        >
          <div className="border-b border-[--color-neutral-200] px-[--padding-sm] py-[--padding-lg]">
            <Dialog.Title asChild>
              <Typography variant="headline020" className="text-[#222526] font-bold">
                YOUR TEAM IS UPDATING
              </Typography>
            </Dialog.Title>
          </div>

          <div className="flex flex-col gap-[--padding-sm] p-[--padding-sm]">
            <Dialog.Description asChild>
              <Typography
                variant="paragraph030"
                className="text-[--color-neutral-900] md:text-14 md:leading-20"
              >
                {description}
              </Typography>
            </Dialog.Description>

            {showImage && (
              <div className="h-[160px] md:h-[200px] w-full overflow-hidden rounded-[--corner-xs] bg-[#f4f4fd]">
                <img
                  src={imageUrl}
                  alt=""
                  aria-hidden
                  className="h-full w-full object-contain"
                  onError={() => setHasImageFailed(true)}
                />
              </div>
            )}

            <Typography variant="paragraph030" className="text-[--color-neutral-900] md:text-14">
              Whilst you wait, see where you finished in the leaderboard this week and keep up to
              date with this weeks fixtures in our match centre.
            </Typography>

            <div className="grid grid-cols-1 gap-[--padding-sm] sm:grid-cols-2">
              <Button variant="contained" color="blue" onClick={onViewLeagues}>
                View My Leagues
              </Button>
              <Button variant="outlined" color="blue" onClick={onViewMatchCentre}>
                View Match Centre
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
